// src/seo/useSEO.js
import { useEffect } from "react";

/**
 * Hook SEO HLBK
 * - title, description, canonical (URL absolue)
 * - Open Graph + Twitter Card
 * - JSON-LD
 * - option noindex
 */

const SITE_NAME = "HLBK — Photographe portraits & événements";
const SITE_URL =
  typeof window !== "undefined" ? window.location.origin : "https://www.hlbk.fr";
const DEFAULT_IMAGE = "/images/og-default.jpg";
const DEFAULT_LOCALE = "fr_FR";
const TWITTER_HANDLE = ""; // ex: "@hlbkphoto" si tu en as un

function ensureAbsolute(url) {
  if (!url) return `${SITE_URL}${DEFAULT_IMAGE}`;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return `${SITE_URL}${url}`;
  try {
    return new URL(url, SITE_URL).toString();
  } catch {
    return `${SITE_URL}${DEFAULT_IMAGE}`;
  }
}

function setUniqueMeta(nameOrProp, isProperty, content) {
  const selector = isProperty
    ? `meta[property="${nameOrProp}"]`
    : `meta[name="${nameOrProp}"]`;

  let el = document.querySelector(selector);
  if (!content) {
    if (el) el.remove();
    return;
  }

  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(isProperty ? "property" : "name", nameOrProp);
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(content));
}

function setCanonicalOnce(href) {
  if (!href) return;
  const abs = ensureAbsolute(href);
  // supprime duplicata
  document
    .querySelectorAll('link[rel="canonical"]')
    .forEach((n, i) => i > 0 && n.remove());
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", abs);
}

function setJsonLd(json) {
  // Supprime l'ancien script si besoin
  const old = document.querySelector(
    'script[type="application/ld+json"][data-seo="true"]'
  );
  if (!json) {
    if (old) old.remove();
    return;
  }

  // Normalisation stricte : tableau d'objets uniquement
  const payload = typeof json === "function" ? json() : json;
  const arr = Array.isArray(payload) ? payload : [payload];

  const cleaned = arr
    .filter((x) => x && typeof x === "object") // vire undefined/null/strings
    .map((o) => {
      // Valeurs par défaut pour éviter les erreurs
      const ctx = o["@context"] || "https://schema.org";
      const type = o["@type"] || "Thing";
      return { "@context": ctx, "@type": type, ...o };
    });

  if (!cleaned.length) {
    if (old) old.remove();
    return;
  }

  const jsonText = JSON.stringify(cleaned);

  let el = old;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo", "true");
    document.head.appendChild(el);
  }
  el.text = jsonText;
}

export default function useSEO({
  title = SITE_NAME,
  description = "Photographe — portraits, événements, entreprises et lieux. Basé en Lorraine (France), Belgique, Luxembourg.",
  canonical, // "/contact" ou `${window.location.origin}/contact`
  image = DEFAULT_IMAGE,
  locale = DEFAULT_LOCALE,
  jsonLd = null, // objet ou tableau d’objets
  noindex = false, // utile en préprod
} = {}) {
  useEffect(() => {
    // Title
    if (title) document.title = title;

    // Canonical (absolu)
    let canonicalUrl = canonical;
    if (!canonicalUrl && typeof window !== "undefined") {
      const { origin, pathname, search } = window.location;
      canonicalUrl = `${origin}${pathname}${search || ""}`;
    }
    setCanonicalOnce(canonicalUrl);

    // Description
    setUniqueMeta("description", false, description);

    // Open Graph
    const absoluteImage = ensureAbsolute(image);
    setUniqueMeta("og:type", true, "website");
    setUniqueMeta("og:site_name", true, SITE_NAME);
    setUniqueMeta("og:title", true, title);
    setUniqueMeta("og:description", true, description);
    setUniqueMeta("og:url", true, canonicalUrl); // <-- déjà absolue
    setUniqueMeta("og:image", true, absoluteImage);
    setUniqueMeta("og:image:secure_url", true, absoluteImage);
    setUniqueMeta("og:image:width", true, "1200");
    setUniqueMeta("og:image:height", true, "630");
    setUniqueMeta("og:locale", true, locale);

    // Twitter
    setUniqueMeta("twitter:card", false, "summary_large_image");
    setUniqueMeta("twitter:title", false, title);
    setUniqueMeta("twitter:description", false, description);
    setUniqueMeta("twitter:image", false, absoluteImage);
    if (TWITTER_HANDLE) setUniqueMeta("twitter:site", false, TWITTER_HANDLE);

    // Robots
    setUniqueMeta("robots", false, noindex ? "noindex,nofollow" : "index,follow");

    // JSON-LD
    setJsonLd(jsonLd);
  }, [title, description, canonical, image, locale, jsonLd, noindex]);
}